import React, { useLayoutEffect, useState } from 'react';
import { Box, ThemeProvider } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import MillosPricingHero from '../marketing/components/millos/MillosPricingHero';
import MillosPricingCompare from '../marketing/components/millos/MillosPricingCompare';
import { marketingColors as C, marketingFonts as F, homepageStyles, homepageTheme } from '../theme_landing_page_2';

const faqData = [
  { q: 'How does pricing work?', a: 'Certificates is £6 per flat per month. Accounting is a fixed £40 per month, whatever the number of flats. Complete is both: £6 per flat plus the £40 accounting fee. Add or remove flats anytime and your bill adjusts.' },
  { q: "What's free?", a: 'Try both agents free for 1 week on up to 10 flats — renewal booking, Open Banking rent tracking and quarterly figures. No payment needed and no card required.' },
  { q: 'Can I take just Certificates or just Accounting?', a: 'Yes. Certificates alone is £6 per flat per month; Accounting alone is £40 per month. Complete runs both on one bill with priority support.' },
  { q: 'What about monthly vs yearly?', a: 'Pay monthly, or choose yearly and get roughly 17% off (two months free). Switch billing anytime.' },
  { q: 'I manage more than 15 flats — what then?', a: 'Once you pass 15 flats we tailor a plan to your portfolio — book a demo and our team will price it against your unit count.' },
];

const PricingPage: React.FC = () => {
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useMarketingFonts();
  useSeoHead({
    title: 'Pricing — Per Flat, Certificates & Accounting | MillOS',
    description: 'Try both MillOS agents free for 1 week on up to 10 flats, with no payment needed. Certificates £6 per flat per month; Accounting £40 per month fixed.',
    canonicalPath: '/pricing',
  });
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <ThemeProvider theme={homepageTheme}>
    <Box className="lv-marketing" sx={{ ...homepageStyles, background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'clip' }}>
      <MarketingNav />

      <MillosPricingHero />

      <MillosPricingCompare />

      <Box component="section" sx={{ padding: { xs: '64px 24px', md: '96px 52px' } }}>
        <Box sx={{ maxWidth: 900, margin: '0 auto' }}>
          <Box component="h2" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(2rem,4vw,3rem)', letterSpacing: '-0.02em', textAlign: 'center', marginBottom: '32px' }}>
            Pricing questions
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {faqData.map((f, i) => (
              <Box key={f.q} sx={{ border: `1px solid ${C.hairline}`, borderRadius: '16px', overflow: 'hidden' }}>
                <Box
                  component="button"
                  type="button"
                  aria-expanded={openFaq === i}
                  aria-controls={`pricing-faq-${i}`}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', padding: '20px 22px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: F.body }}
                >
                  <Box sx={{ fontSize: 16.5, fontWeight: 500, color: C.ink }}>{f.q}</Box>
                  <Box sx={{ flex: 'none', fontSize: 22, color: C.accent, transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform .2s' }}>+</Box>
                </Box>
                {openFaq === i && <Box id={`pricing-faq-${i}`} sx={{ padding: '0 22px 22px', fontSize: 15.5, lineHeight: 1.65, color: C.body }}>{f.a}</Box>}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <MarketingFooter />
    </Box>
    </ThemeProvider>
  );
};

export default PricingPage;
